import React, { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate } from 'react-router-dom'
import { Content, Indicators, Layout, Responsive, Navigation } from 'ui'
import { usePaginatedEvents } from './usePaginatedEvents'
import { EventNavigationDrawer } from './EventNavigationDrawer'
import { useFindEvent } from '../../../queries/useEventOperations'
import { CreateEventStepOneForm } from '../../AppNavigator/Events/CreateEventStepOneForm'
import { CreateEventStepTwoForm } from '../../AppNavigator/Events/CreateEventStepTwoForm'
import { CreateEventStepThreeForm } from '../../AppNavigator/Events/CreateEventStepThreeForm'
import { CreateEventStepFourForm } from '../../AppNavigator/Events/CreateEventStepFourForm'

export const EventEditView: React.FC = () => {
  // Drawer appearance
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('compact')

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { locationSearch } = usePaginatedEvents({ fetchPolicy: 'cache-first' })

  // State for managing the active tab
  const [currentTab, setCurrentTab] = useState('basics')

  // Fetch event data using React Query hook
  const { data, isLoading } = useFindEvent(id ?? '')
  const event = data?.response?.event

  // Handle tab selection
  const handleTabSelect = ({ value }: Content.ITab) => {
    setCurrentTab(value)
  }

  return (
    <div
      className="w-full flex-grow flex-col flex sm:flex-row flex"
      style={{ opacity: isLoading ? 0.5 : 1 }}
    >
      <Helmet
        title={`Editing: ${event ? event.name : 'Event'} - MCSS Pro Connect`}
      />
      <EventNavigationDrawer />
      <Layout.PaddedContent className="flex-grow w-full overflow-y-auto bg-contentareabackgroundalt">
        <div className="flex-col flex md:flex-row flex pb-32">
          <article className="flex-col flex w-full sm:w-3/4 mr-0 sm:mr-4">
            <Content.Body>
              <Navigation.ReturnLink
                to={`/admin/events/show/${id}${locationSearch}`}
              >
                Return to Event
              </Navigation.ReturnLink>
            </Content.Body>
            <Content.Heading className="mb-4">
              {isLoading ? (
                <div className="flex">
                  <div className="mr-auto">
                    <Indicators.Spinner />
                  </div>
                </div>
              ) : (
                `Editing Event: ${event ? event.name : ''}`
              )}
            </Content.Heading>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Indicators.Spinner size="lg" />
              </div>
            ) : (
              <>
                <Content.Tabs
                  currentValue={currentTab}
                  tabs={[
                    { label: 'Basics', value: 'basics' },
                    { label: 'Where & When', value: 'whenWhere' },
                    { label: 'Keywords', value: 'keywords' },
                    { label: 'Description & Publish', value: 'publish' },
                  ]}
                  onSelectTab={handleTabSelect}
                  className="mb-4"
                />

                {currentTab === 'basics' && (
                  <div className="bg-white p-6 rounded-lg shadow mt-4">
                    <CreateEventStepOneForm existingEvent={event} />
                  </div>
                )}

                {currentTab === 'whenWhere' && (
                  <div className="bg-white p-6 rounded-lg shadow mt-4">
                    <CreateEventStepTwoForm
                      eventId={id ?? ''}
                      existingEvent={event}
                      isLoading={isLoading}
                    />
                  </div>
                )}

                {currentTab === 'keywords' && (
                  <div className="bg-white p-6 rounded-lg shadow mt-4">
                    <CreateEventStepThreeForm
                      eventId={id ?? ''}
                      existingEvent={event}
                      isLoading={isLoading}
                    />
                  </div>
                )}

                {currentTab === 'publish' && (
                  <div className="bg-white p-6 rounded-lg shadow mt-4">
                    <CreateEventStepFourForm eventId={id ?? ''} />
                  </div>
                )}
              </>
            )}
          </article>
        </div>
      </Layout.PaddedContent>
    </div>
  )
}
